import type { FC, ChangeEventHandler, EventHandler, MouseEvent } from "react"

import { PrettyUnit, UNIT_OF_MEASURE } from "@logic/lib/powur-os"
import styled, { css } from "styled-components"
import { fontWeight } from "styled-system"

import { DEVICE } from "~/constants/device-widths"
import { formatCurrency } from "~/utils/finance/format"
import { replaceCharsForCY } from "~/utils/replaceCharsForCY"

export const StyledForm = styled.form`
  position: relative;
  height: 100%;
`

export const Container = styled.div`
  display: flex;
  flex-direction: row;
  background-color: var(--white);
  position: relative;
  height: calc(95vh - 149px);
  width: 100%;
  overflow: hidden;
`
export const CardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-top: 1px solid var(--grey-light);
  gap: 20px;
  max-height: 100%;
  padding: 10px 25px;
  margin-top: 20px;
  overflow-y: auto;
  background-color: var(--white);
`
export const Adders = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;
  .no-adders {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 535px;
    align-self: center;
    text-align: center;
    gap: 18px;
    p {
      font-size: 13px;
      line-height: 16px;
      ${fontWeight(600)}
      color: #C0C1C7
    }
  }
  .form-footer {
    width: 100%;
    min-height: 75px;
  }
`
export const CardWrapper = styled.div<{
  allowDisabledToggle: boolean | undefined
  selected: boolean | null | undefined
  disabled: boolean | null | undefined
  invalid: boolean | null | undefined
}>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  border-radius: 12px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  overflow-y: auto;
  width: 100%;
  max-width: 100%;
  min-height: 200px;
  height: fit-content;
  @media ${DEVICE.laptop} {
    width: 40%;
    max-width: 47.5%;
    height: 200px;
    min-height: unset;
  }
  span {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: var(--blue) solid 1px;
    background-color: var(--white);
  }
  &.has-price {
    border: 1px solid
      ${({ invalid }) => (invalid ? `var(--danger-faded)` : `var(--blue-faded)`)};
    :hover {
      box-shadow: 0px 0px 15px
        ${({ invalid }) =>
          invalid ? `var(--danger-faded)` : `var(--blue-faded)`};
      ${({ allowDisabledToggle, disabled }) =>
        (!disabled || allowDisabledToggle) &&
        css`
          cursor: pointer;
        `}
    }
    :active {
      ${({ allowDisabledToggle, disabled }) =>
        (!disabled || allowDisabledToggle) &&
        css`
          transform: scale(0.99);
        `}
    }
  }
  ${(props) =>
    props.selected &&
    css`
      transform: scale(1.02);
      border: 2px solid ${props.invalid ? `var(--danger)` : `var(--blue)`};
      &.has-price {
        :hover {
          border: 2px solid ${props.invalid ? `var(--danger)` : `var(--blue)`};
        }
      }
      span {
        background-color: ${props.invalid ? `var(--danger)` : `var(--blue)`};
        ::after {
          content: "";
          display: block;
          position: relative;
          left: 6px;
          top: -8px;
          display: inline-block;
          transform: rotate(45deg);
          height: 8px;
          width: 3px;
          border-bottom: 2px solid var(--white);
          border-right: 2px solid var(--white);
        }
      }
    `}
  ${(props) =>
    props.selected &&
    props.disabled &&
    css`
      background-color: var(--light-blue);
      opacity: 0.5;
      cursor: not-allowed;
      span {
        background-color: ${props.invalid ? `var(--danger)` : `var(--blue)`};
      }
    `}
  .first-row {
    display: flex;
    justify-content: space-between;
  }
  h3 {
    display: flex;

    overflow: hidden;
    max-width: 85%;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-bottom: 5px;
    max-height: 100%;
    @media ${DEVICE.tablet} {
      max-height: 120px;
    }
  }
  .price-display {
    width: 100%;
    display: flex;
  }
  h3,
  .price-display,
  input {
    font-family: PoppinsVF;
    font-size: 18px;
    line-height: 21px;
    ${fontWeight(600)}
  }
  input {
    margin-right: 1rem;
    width: 50%;
    text-align: right;
  }
  p {
    font-size: 13px;
    line-height: 16px;
  }
  .price-display,
  input {
    min-width: 61px;
    max-height: 70px;
    position: relative;
    bottom: 0;
    color: ${({ invalid }) => (invalid ? `var(--danger)` : `var(--blue)`)};
    padding: 6px 0px;
    border-radius: 4px;
  }
  input {
    border: 1px solid
      ${({ invalid }) => (invalid ? `var(--danger)` : `var(--blue)`)};
    background-color: var(--white);
    height: 32px;
    :focus {
      border: 1px solid
        ${({ invalid }) => (invalid ? `var(--danger)` : `var(--blue)`)};
      box-shadow: 0px 0px 15px var(--blue-faded);
    }
  }
  ${({ allowDisabledToggle, disabled }) =>
    disabled &&
    css`
      cursor: ${allowDisabledToggle ? `pointer` : `not-allowed`};
      opacity: 0.5;
    `}
  ${({ invalid }) =>
    invalid &&
    css`
      input {
        border: 1px solid var(--danger);
        color: var(--danger);
        :focus {
          border: 1px solid var(--danger);
          box-shadow: 0px 0px 15px var(--danger-faded);
        }
      }
      span {
        border: 1px solid var(--danger);
      }
    `}
`

export const Unit = styled.div`
  font-family: PoppinsVF;
  font-size: 18px;
  line-height: 21px;
  ${fontWeight(600)}
  height: 100%;
  width: 30px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

export const Card: FC<{
  allowDisabledToggle?: boolean
  id: string
  selected: boolean
  cost?: number | null
  title: string
  toggle: EventHandler<MouseEvent>
  unit?: number | null
  value?: number
  isVariableInput?: boolean
  onChange?: ChangeEventHandler<HTMLInputElement>
  disabled?: boolean
  invalid?: boolean
  testData?: number | string
}> = ({
  allowDisabledToggle,
  id,
  selected,
  cost,
  title,
  unit,
  value,
  toggle,
  onChange,
  isVariableInput,
  disabled,
  invalid,
  testData,
}) => {
  const numberInput = document.getElementById(`feed-${id}`)
  numberInput?.addEventListener(`keydown`, (e) => {
    if (e.key === `e` || (unit && e.key === `.`)) e.preventDefault()
  })

  const getValue = () => {
    if (!value && selected) return 0
    if (!value && !selected) return ``

    return value
  }

  return (
    <CardWrapper
      allowDisabledToggle={allowDisabledToggle}
      disabled={disabled}
      invalid={invalid}
      key={id}
      selected={selected}
      onClick={
        cost !== null && (!disabled || allowDisabledToggle) ? toggle : undefined
      }
      className={onChange || typeof cost === `number` ? `has-price` : `no-price`}
    >
      <div>
        <div className="first-row">
          <h3
            data-testid={replaceCharsForCY(title)}
            data-info={id}
            data-cy={testData}
          >
            {title}
          </h3>
          <span />
        </div>
      </div>

      <div
        className="price-display"
        data-testid={`${replaceCharsForCY(title)}-price`}
      >
        {isVariableInput ? (
          <>
            <Unit>$</Unit>
            <input
              style={{ width: `100%`, maxWidth: `150px` }}
              type="number"
              min={0}
              step={1}
              id={`feed-${id}`}
              value={getValue()}
              onClick={(e) => e.stopPropagation()}
              onChange={disabled ? undefined : onChange}
              data-testid={`${replaceCharsForCY(title)}-input`}
            />
          </>
        ) : (
          <>
            {typeof cost === `number`
              ? `${formatCurrency(Number(cost))}  / ${
                  PrettyUnit[UNIT_OF_MEASURE[unit ?? 0]]
                }`
              : ``}
          </>
        )}
      </div>

      {!isVariableInput && onChange && (
        <div style={{ display: `flex` }}>
          {typeof cost !== `number` && <Unit>$</Unit>}
          <input
            style={{ width: `100%`, maxWidth: `150px` }}
            type="number"
            min={0}
            step={1}
            id={`feed-${id}`}
            value={getValue()}
            onClick={(e) => e.stopPropagation()}
            onChange={disabled ? undefined : onChange}
          />
          {unit ? <Unit>{PrettyUnit[UNIT_OF_MEASURE[unit ?? 0]]}</Unit> : ``}
        </div>
      )}
    </CardWrapper>
  )
}
